import { useState, useMemo } from "react";
import { Box, Typography, TextField, Slider, InputAdornment, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { useData } from "../context/DataContext";

const MIN_ROW_H = 7;
const MAX_ROW_H = 40;
const TEXT_THRESHOLD = 18;
const NAME_WIDTH_MIN = 72;
const NAME_WIDTH_MAX = 200;

function rowHeight(zoom: number) {
  return MIN_ROW_H + ((MAX_ROW_H - MIN_ROW_H) * zoom) / 100;
}
function nameWidth(zoom: number) {
  return NAME_WIDTH_MIN + ((NAME_WIDTH_MAX - NAME_WIDTH_MIN) * zoom) / 100;
}

function getColor(value: number | null) {
  if (value === null || value === 0) return "hsl(222 30% 88%)";
  if (value < 0.9) return "hsl(142 78% 36%)";
  if (value < 1.0) {
    const t = (value - 0.9) / 0.1;
    const h = Math.round(142 - t * 22);
    const s = Math.round(78 - t * 13);
    const l = Math.round(36 - t * 3);
    return `hsl(${h} ${s}% ${l}%)`;
  }
  const t = Math.min((value - 1.0) / 1.0, 1);
  const h = Math.round(120 * (1 - t));
  const s = Math.round(65 + t * 20);
  const l = t < 0.5 ? Math.round(33 + t * 36) : Math.round(69 - t * 39);
  return `hsl(${h} ${s}% ${l}%)`;
}

interface HeatmapProps {
  onNavigateToVessel: (vessel: string) => void;
}

export default function Heatmap({ onNavigateToVessel }: HeatmapProps) {
  const dashboardData = useData();
  const { heatmapData, heatmapHeaders } = dashboardData;
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(0);

  const rh = rowHeight(zoom);
  const nw = nameWidth(zoom);
  const showText = rh >= TEXT_THRESHOLD;

  const sortedData = useMemo(() => {
    const sorted = [...heatmapData].sort((a, b) => {
      const aVal = a.values[a.values.length - 1]?.value ?? 0;
      const bVal = b.values[b.values.length - 1]?.value ?? 0;
      return bVal - aVal;
    });
    if (search.trim() === "") return sorted;
    return sorted.filter(r => r.site.toLowerCase().includes(search.toLowerCase()));
  }, [heatmapData, search]);

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "calc(100vh - 220px)" }}>
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Fleet Heatmap</Typography>
          <Typography variant="body2" color="text.secondary">
            Auth (Captive) GB per user — {sortedData.length} vessels
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Filter vessels..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 220 }}
          />
          <Box display="flex" alignItems="center" gap={1} sx={{ width: 180 }}>
            <ZoomOutIcon fontSize="small" color="disabled" />
            <Slider size="small" value={zoom} onChange={(_, v) => setZoom(v as number)} min={0} max={100} />
            <ZoomInIcon fontSize="small" color="disabled" />
          </Box>
          <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: 12, color: "text.secondary" }}>
            <span>0</span>
            <Box sx={{ width: 80, height: 10, borderRadius: 0.5, background: "linear-gradient(to right, hsl(160,80%,48%), hsl(36,80%,48%), hsl(0,80%,48%))" }} />
            <span>1.6+ GB</span>
          </Box>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box display="flex" sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100", fontSize: showText ? 11 : 9 }}>
          <Box sx={{ width: nw, minWidth: nw, px: 1, py: 0.5, fontWeight: 600, textTransform: "uppercase", borderRight: "1px solid", borderColor: "divider" }}>
            Vessel
          </Box>
          {heatmapHeaders.map(h => (
            <Box key={h} sx={{ flex: 1, textAlign: "center", py: 0.5, fontWeight: 600, textTransform: "uppercase", borderLeft: "1px solid", borderColor: "divider" }}>
              {h}
            </Box>
          ))}
        </Box>

        <Box sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
          {sortedData.map(row => (
            <Box key={row.site} sx={{ display: "flex", height: rh, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box
                onClick={() => onNavigateToVessel(row.site)}
                sx={{
                  width: nw, minWidth: nw, display: "flex", alignItems: "center", px: 1,
                  borderRight: "1px solid", borderColor: "divider", fontFamily: "monospace",
                  fontSize: Math.max(8, rh * 0.55), whiteSpace: "nowrap", overflow: "hidden",
                  cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" },
                }}
                title={`Open ${row.site} in Ship Analysis`}
              >
                {row.site}
              </Box>
              {row.values.map((v, j) => (
                <Box
                  key={j}
                  onClick={() => onNavigateToVessel(row.site)}
                  title={`${row.site} — ${heatmapHeaders[j]}: ${v.value != null ? v.value.toFixed(2) + " GB/user" : "—"}`}
                  sx={{
                    flex: 1, p: "1px", cursor: "pointer",
                    borderLeft: "1px solid", borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                      backgroundColor: getColor(v.value), fontSize: Math.max(7, rh * 0.45),
                      color: "rgba(255,255,255,0.9)", fontFamily: "monospace", fontWeight: 500,
                    }}
                  >
                    {showText && v.value !== null ? v.value.toFixed(2) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
          {sortedData.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
              No vessels match "{search}"
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
